import type { FunctionComponent, SetStateAction } from 'react';
import { useMemo, useState } from 'react';
import type { DependencyMetadataFilterOption, FormatName, IncludeFilterOption } from '../../types';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../../../../../../generic/Button';
import { SidebarLayout } from '../../../../../../layouts';
import { FilterPanel, FormLabelVariant, LabeledSelect } from '../../../../../../generic';
import type { SelectOption, SetState } from '../../../../../../../types';
import styles from './Filter.module.less';
import IncludeCheckboxGroup from './IncludeCheckboxGroup';
import DependencyMetadataCheckboxGroup from './DependencyMetadataCheckboxGroup';
import type { DependencyInfoOption, ReportCategory } from '../../../../shared/types';
import RegulatoryComplianceToggles from './RegulatoryComplianceToggles';
import { DependencyInfoOptions, ReportCategories, ReportOptionDetails } from '../../../../ReportGeneration/utils';
import { licenseIncludeOptions } from '../../constants';
import PaidFeatureGate from '../../../../../../generic/Gates/PaidFeatureGate';
import { isFreeTier } from '../../../../../../../../shared/subscriptions';
import { window } from '../../../../../../../globals';
import FloatingToolbar from '../../../../../../generic/FloatingToolbar';
import Flex from '../../../../../../generic/Flex';

export type FormatSelection = SelectOption<FormatName>;

type FormatSelectProps = {
  format: FormatSelection;
  formats: FormatSelection[];
  setFormat: SetState<FormatSelection>;
};

const FormatSelect: FunctionComponent<FormatSelectProps> = ({ format, formats, setFormat }: FormatSelectProps) => (
  <div className="width-full" data-test-id="report-filters">
    <LabeledSelect
      variant={FormLabelVariant.Filter}
      label="Format"
      value={format}
      options={formats}
      id="report-format"
      onChange={(option: FormatSelection | null) => {
        if (option) {
          setFormat(option);
        }
      }}
    />
  </div>
);

type FilterState = {
  format: FormatName;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyInfoOption[];
};

export type OnApply = (args: FilterState) => void;

type Props = {
  category: ReportCategory;
  appliedFormat: FormatName | null;
  format: FormatSelection;
  setFormat: SetState<FormatSelection>;
  formats: FormatSelection[];
  onApply: OnApply;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  setIsDirty: SetState<boolean>;
  isApplyButtonDisabled: boolean;
};

const Filter = ({
  category,
  format,
  appliedFormat,
  setFormat,
  formats,
  onApply,
  setIsDirty,
  isApplyButtonDisabled,
  includeOptions: appliedIncludeOptions,
  dependencyMetadataOptions: appliedDependencyMetadataOptions,
}: Props) => {
  const isPaidFeatureGateRestricted = isFreeTier({
    plan: window?.user?.organization?.stripe_plan,
    stripePriceLimit: window?.user?.organization?.stripePriceLimit,
  });
  const initialFlatIncludeValues = useMemo(
    () =>
      appliedIncludeOptions
        .filter((option) => !licenseIncludeOptions.has(option))
        .map((option) => ({ value: option, label: ReportOptionDetails[option]?.title })),
    [appliedIncludeOptions],
  );
  const [flatIncludeValues, setFlatIncludeValues] = useState<SelectOption[]>(initialFlatIncludeValues);
  const initialIncludeCheckboxLicenseValues = useMemo(
    () =>
      appliedIncludeOptions
        .filter((option) => licenseIncludeOptions.has(option))
        .map((option) => ({ value: option, label: ReportOptionDetails[option]?.title })),
    [appliedIncludeOptions],
  );
  const [includeCheckboxLicenseValues, setIncludeCheckboxLicenseValues] = useState<SelectOption[]>(
    initialIncludeCheckboxLicenseValues,
  );
  const initialDependencyMetadataCheckboxValues = useMemo(
    () =>
      appliedDependencyMetadataOptions.map((option) => ({
        value: option,
        label: DependencyInfoOptions[option],
      })),
    [appliedDependencyMetadataOptions],
  );
  const [dependencyMetadataCheckboxValues, setDependencyMetadataCheckboxValues] = useState<SelectOption[]>(
    initialDependencyMetadataCheckboxValues,
  );
  // Add a helper function to wrap any set function to set isDirty to true
  function withSetIsDirty<T>(fn: (value: SetStateAction<T>) => void) {
    return (value: SetStateAction<T>) => {
      setIsDirty(true);
      fn(value);
    };
  }
  const includeOptions = [...flatIncludeValues, ...includeCheckboxLicenseValues].map(
    ({ value }) => value as IncludeFilterOption,
  );
  const dependencyMetadataOptions = dependencyMetadataCheckboxValues.map(({ value }) => value as DependencyInfoOption);
  const onClickApply = () => {
    setIsDirty(false);
    onApply({ format: format.value, includeOptions, dependencyMetadataOptions });
  };
  const title = (
    <div className={styles.filterTitle}>
      <span>Customization</span>
    </div>
  );

  function discardFormat() {
    if (appliedFormat) {
      setFormat({ label: appliedFormat, value: appliedFormat });
    } else if (formats[0]) {
      // This happens when user has not applied any format.
      // We need to reset it to the first format in the list.
      setFormat(formats[0]);
    }
  }

  const isFixPlanReport = category === ReportCategories.FixPlan;

  return (
    <SidebarLayout.Sidebar>
      <FilterPanel title={title}>
        <FormatSelect format={format} formats={formats} setFormat={withSetIsDirty(setFormat)} />
        {!isFixPlanReport && (
          <>
            <PaidFeatureGate isRestricted={isPaidFeatureGateRestricted} hidePill>
              <RegulatoryComplianceToggles
                setFlatIncludeValues={withSetIsDirty(setFlatIncludeValues)}
                setIncludeCheckboxLicenseValues={withSetIsDirty(setIncludeCheckboxLicenseValues)}
                setDependencyMetadataCheckboxValues={withSetIsDirty(setDependencyMetadataCheckboxValues)}
                flatIncludeValues={flatIncludeValues}
                includeCheckboxLicenseValues={includeCheckboxLicenseValues}
                dependencyMetadataCheckboxValues={dependencyMetadataCheckboxValues}
          />
            </PaidFeatureGate>
            <IncludeCheckboxGroup
              format={format}
              category={category}
              values={flatIncludeValues}
              setValues={withSetIsDirty(setFlatIncludeValues)}
              licenseValues={includeCheckboxLicenseValues}
              setLicenseValues={withSetIsDirty(setIncludeCheckboxLicenseValues)}
        />
            <PaidFeatureGate isRestricted={isPaidFeatureGateRestricted} hidePill>
              <DependencyMetadataCheckboxGroup
                values={dependencyMetadataCheckboxValues}
                setValues={withSetIsDirty(setDependencyMetadataCheckboxValues)}
          />
            </PaidFeatureGate>
          </>
        )}
      </FilterPanel>
      <FloatingToolbar
        shouldShow={!isApplyButtonDisabled}
        text="Your changes have not been applied."
        actions={
          <Flex gap="8px" wrap="wrap">
            <Button onClick={onClickApply} type={BUTTON_TYPE.PRIMARY} size={BUTTON_SIZE.MEDIUM}>
              Apply
            </Button>
            <Button
              onClick={() => {
                setFlatIncludeValues(initialFlatIncludeValues);
                setIncludeCheckboxLicenseValues(initialIncludeCheckboxLicenseValues);
                setDependencyMetadataCheckboxValues(initialDependencyMetadataCheckboxValues);
                discardFormat();
                setIsDirty(false);
              }}
              type={BUTTON_TYPE.SLATE}
              size={BUTTON_SIZE.MEDIUM}
            >
              Discard
            </Button>
          </Flex>
        }
      />
    </SidebarLayout.Sidebar>
  );
};

export default Filter;
